import { For, Show, createEffect, onMount } from "solid-js";
import { linkClick } from "../core/router";
import { ArrowLeftIcon, ArrowRightIcon, CloseIcon, HelpIcon, PrintIcon, StepIcon } from "./Icons";

export const guideSteps = ["measurements", "library", "workspace", "print"] as const;
export type GuideStep = (typeof guideSteps)[number];

interface StepContent {
  title: string;
  /** One line: what finishing this step gets you. */
  goal: string;
  /** Label for the button that moves on once the goal is met. */
  next?: string;
  todo: string[];
  tips: string[];
}

/** The user guide: one entry per step, in the order the app walks through them. */
export const guide: Record<GuideStep, StepContent> = {
  measurements: {
    title: "Measurements",
    goal: "Set the figure the garment is cut for. Every pattern reads from it.",
    next: "Choose a pattern",
    todo: [
      "Pick a saved profile, or type new values into any measurement.",
      "Hover, tap or edit a measurement to see where it is taken on the mannequin.",
      "Replace anything marked est. (estimated) with a real measurement when one is available. Estimates are a starting point.",
      "Switch the mannequin between male and female figures if the profile's figure is wrong.",
    ],
    tips: [
      "The measurements are saved in this browser and shared by every pattern, so they only need entering once.",
      "Download measurements keeps a copy you can reuse elsewhere.",
      "The YAML editor at the bottom is for editing a pattern's drafting rules. You do not need it to make a garment.",
    ],
  },
  library: {
    title: "Choose a pattern",
    goal: "Pick the garment you want to make.",
    next: "Open the workspace",
    todo: [
      "Browse the patterns, grouped by garment type.",
      "Open a pattern to draft it straight away from the measurements.",
    ],
    tips: [
      "You can come back and switch patterns at any time. The measurements stay the same.",
    ],
  },
  workspace: {
    title: "Workspace",
    goal: "Choose the style and check the pieces before you print.",
    next: "Print the pattern",
    todo: [
      "Change style options on the left. The pieces redraft as you change them.",
      "Turn on Body to see the figure inside each piece. The gap to the cut line is the ease.",
      "Check Sew-line fit and Book checks on the right. Green means the seams that join will match.",
      "Click a point to see how it was drafted and which measurements it uses.",
      "Click the printer on a piece (or beside it in the Pieces list) to leave it out of the print, such as pockets you already have.",
    ],
    tips: [
      "Scroll or pinch to zoom, drag to pan, and double-click or double-tap a piece to focus on it.",
      "Measure draws a tape between two points, and it snaps to nearby pattern points.",
      "If a piece looks wrong, check the measurements first. Most problems start there.",
    ],
  },
  print: {
    title: "Print",
    goal: "Print the sheets at actual size, then tape them together.",
    next: "Print / save PDF",
    todo: [
      "Print the cover page first, at 100% or “Actual size”, with browser headers and footers off.",
      "Measure the test square on the cover. If it is not exactly 50 mm, fix the print scale before printing the rest.",
      "Lay the sheets out using the assembly map. Neighbouring sheets overlap by 10 mm.",
      "Line up the grid, the registration crosses and the rainbow lines (match each colour and number or letter), then tape.",
    ],
    tips: [
      "Blank and near-blank sheets (under 5% covered) are skipped, so their places on the map stay empty; finish any line that ran onto one by hand.",
      "The configuration sheet records the measurements and options used, so you can redraft the same pattern later.",
    ],
  },
};

/** The current step's goal, with a button to the next step. */
export function GoalBar(props: { step: GuideStep; nextLabel?: string; nextHref?: string; onNext?: () => void; onHelp: () => void }) {
  const index = () => guideSteps.indexOf(props.step);
  const content = () => guide[props.step];
  const nextLabel = () => props.nextLabel ?? content().next;
  return (
    <div class="goal-bar no-print" role="region" aria-label="Current step">
      <span class="goal-step"><StepIcon step={props.step} size={15} />Step {index() + 1} of {guideSteps.length}</span>
      <p class="goal-text"><strong>Goal:</strong> {content().goal}</p>
      <button type="button" class="link-button goal-help" onClick={props.onHelp}><HelpIcon size={16} />How do I do this?</button>
      <Show when={nextLabel()}>
        <Show when={props.nextHref} fallback={<button type="button" class="primary goal-next" onClick={props.onNext}><PrintIcon size={16} />{nextLabel()}</button>}>
          {(href) => <a class="button primary goal-next" href={href()} onClick={linkClick}>{nextLabel()}<ArrowRightIcon size={16} /></a>}
        </Show>
      </Show>
    </div>
  );
}

/** The user guide in a modal, opened at one step and able to move between them. */
export function GuideDialog(props: { step?: GuideStep; hrefFor: (step: GuideStep) => string; onStep: (step: GuideStep) => void; onClose: () => void }) {
  let dialog!: HTMLDialogElement;
  createEffect(() => {
    if (props.step && !dialog.open) dialog.showModal();
    if (!props.step && dialog.open) dialog.close();
  });
  onMount(() => dialog.addEventListener("close", props.onClose));
  const index = () => (props.step ? guideSteps.indexOf(props.step) : 0);
  const content = () => guide[props.step ?? "measurements"];
  const go = (event: MouseEvent & { currentTarget: HTMLAnchorElement }) => { linkClick(event); props.onClose(); };

  return (
    <dialog ref={dialog} class="guide-dialog" aria-labelledby="guide-title" onClick={(event) => { if (event.target === dialog) props.onClose(); }}>
      <div class="guide-body">
        <header class="guide-header">
          <span class="guide-step-icon"><StepIcon step={props.step ?? "measurements"} size={26} /></span>
          <div class="guide-title"><p class="eyebrow">USER GUIDE · STEP {index() + 1} OF {guideSteps.length}</p><h2 id="guide-title">{content().title}</h2></div>
          <button type="button" class="guide-close" aria-label="Close guide" onClick={props.onClose}><CloseIcon size={18} /></button>
        </header>
        <nav class="guide-steps" aria-label="Guide steps">
          <For each={guideSteps}>{(step, i) => (
            <button type="button" classList={{ active: step === props.step }} aria-current={step === props.step ? "step" : undefined} onClick={() => props.onStep(step)}>
              <StepIcon step={step} size={16} /><span class="guide-step-label">{i() + 1}. {guide[step].title}</span>
            </button>
          )}</For>
        </nav>
        <p class="guide-goal"><strong>Goal:</strong> {content().goal}</p>
        <h3>What to do</h3>
        <ol><For each={content().todo}>{(item) => <li>{item}</li>}</For></ol>
        <h3>Good to know</h3>
        <ul><For each={content().tips}>{(item) => <li>{item}</li>}</For></ul>
        <footer class="guide-footer">
          <button type="button" disabled={index() === 0} onClick={() => props.onStep(guideSteps[index() - 1])}><ArrowLeftIcon size={16} />Previous</button>
          <a class="button" href={props.hrefFor(props.step ?? "measurements")} onClick={go}>Go to this step</a>
          <Show when={index() < guideSteps.length - 1} fallback={<button type="button" class="primary" onClick={props.onClose}>Got it</button>}>
            <button type="button" class="primary" onClick={() => props.onStep(guideSteps[index() + 1])}>Next<ArrowRightIcon size={16} /></button>
          </Show>
        </footer>
      </div>
    </dialog>
  );
}
